/**
 * The first-run journey, as data.
 *
 * A vendor's first minutes: say hello, give Ardi the website, then answer a
 * few short questions while Ardi reads the business in the background — the
 * brand off the site, the search market off DataForSEO, the shop platform off
 * the page source. What Ardi has learned so far is kept as a "notebook" that
 * the screen draws beside the questions, so the vendor watches it fill in.
 *
 * Everything here is pure: the streams and polls live in the hook, and this
 * module only folds what they report into the notebook and decides what to
 * suggest and prepare. Tested without a network.
 */
import type { AnalysisRun } from '../../../common/hooks/use-vendor-analysis-api.hook';
import type { PlatformDetection } from '../../../common/hooks/use-commerce-api.hook';

export const JOURNEY_STEPS = ['welcome', 'website', 'learning', 'socials', 'store', 'goals', 'preparing'] as const;
export type JourneyStep = (typeof JOURNEY_STEPS)[number];

/** The chapters shown in the top bar — a step is one screen, a chapter is what the vendor counts. */
export const CHAPTERS: { id: string; steps: JourneyStep[] }[] = [
    { id: 'hello', steps: ['welcome'] },
    { id: 'website', steps: ['website', 'learning'] },
    { id: 'channels', steps: ['socials', 'store'] },
    { id: 'goals', steps: ['goals'] },
    { id: 'ready', steps: ['preparing'] },
];

export const chapterOf = (step: JourneyStep): number => CHAPTERS.findIndex((c) => c.steps.includes(step));

export const nextStep = (step: JourneyStep): JourneyStep =>
    JOURNEY_STEPS[Math.min(JOURNEY_STEPS.indexOf(step) + 1, JOURNEY_STEPS.length - 1)];

/**
 * `websiteKnown`: the vendor gave its website before the journey (the
 * self-serve signup asks for it first), so the journey never asks again —
 * hello, then straight to the reading.
 */
export const previousStep = (step: JourneyStep, { websiteKnown = false }: { websiteKnown?: boolean } = {}): JourneyStep => {
    const i = JOURNEY_STEPS.indexOf(step);
    const prev = JOURNEY_STEPS[Math.max(0, i - 1)];
    if (websiteKnown) {
        // No website question to go back to: the reading, and before it hello.
        return prev === 'website' ? 'welcome' : prev;
    }
    // "learning" is a moment, not a question: back from socials returns to the website.
    return prev === 'learning' ? 'website' : prev;
};

/** Where "Let's start" on the hello screen goes. */
export const stepAfterWelcome = ({ websiteKnown = false }: { websiteKnown?: boolean } = {}): JourneyStep => (websiteKnown ? 'learning' : 'website');

/** What decides whether the journey is offered, read from the profile (and the signup, for self-serve accounts). */
export interface JourneyFacts {
    onboarded: boolean;
    websiteUrl?: string;
    /** A journey started on this browser and not finished. */
    inProgress: boolean;
}

/**
 * Whether to offer the journey: `undefined` while still unknown.
 *
 * First run: no journey finished, and either no website yet (a vendor
 * created by Ardealy — the only way anyone had arrived before), a journey
 * started here and not finished, or a self-serve account (it gave its
 * website at signup, and the journey is what it signed up for). A vendor
 * who had a website before the journey existed is never sent through it.
 * `facts === null` means "do not offer" (skipped, support, unreadable profile).
 */
export function shouldOfferJourney(facts: JourneyFacts | null | undefined, selfServe: boolean | undefined): boolean | undefined {
    if (facts === undefined) return undefined;
    if (!facts || facts.onboarded) return false;
    if (!facts.websiteUrl?.trim() || facts.inProgress) return true;
    if (selfServe === undefined) return undefined;
    return selfServe;
}

/** The workspaces a vendor can come for (Home is always there). */
export const GOALS = ['visualizer', 'assistant', 'marketing', 'business', 'ads'] as const;
export type Goal = (typeof GOALS)[number];

export const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'pinterest', 'tiktok', 'linkedin'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export type LogKind = 'site' | 'pages' | 'social' | 'pictures' | 'scenes' | 'brand' | 'market' | 'keywords' | 'competitors' | 'backlinks' | 'shop';

/** One line of what Ardi is doing — localised by the screen from its kind and params. */
export interface LogLine {
    id: string;
    kind: LogKind;
    status: 'running' | 'done' | 'failed';
    params?: Record<string, string | number>;
}

export interface Notebook {
    website?: string;
    screenshot?: string;
    brand: {
        name?: string;
        logoUrl?: string;
        colors: string[];
        font?: string;
        tone?: string;
        keywords: string[];
        promise?: string;
    };
    socials: Partial<Record<SocialPlatform, { handle: string; followers?: number; source: 'site' | 'given' }>>;
    market: {
        keywords?: number;
        visits?: number;
        top10?: number;
        trafficValue?: number;
        competitors: string[];
        topKeyword?: string;
    };
    shop?: { platform: string | null; confidence: 'certain' | 'likely' | null; connectable: boolean };
    log: LogLine[];
    brandDone: boolean;
    marketDone: boolean;
}

export const emptyNotebook = (website?: string): Notebook => ({
    website,
    brand: { colors: [], keywords: [] },
    socials: {},
    market: { competitors: [] },
    log: [],
    brandDone: false,
    marketDone: false,
});

const HEX = /^#[0-9a-f]{6}$/i;

/** The lines brand discovery writes; the search read's lines are settled by its own poll. */
const BRAND_KINDS = new Set<LogKind>(['site', 'pages', 'social', 'pictures', 'scenes', 'brand']);

/** A new brand phase started, so every earlier brand line still spinning has finished. */
function settle(log: LogLine[], to: LogLine['status'] = 'done'): LogLine[] {
    return log.map((l) => (l.status === 'running' && BRAND_KINDS.has(l.kind) ? { ...l, status: to } : l));
}

function upsert(log: LogLine[], line: LogLine): LogLine[] {
    const i = log.findIndex((l) => l.id === line.id);
    if (i < 0) return [...log, line];
    const next = [...log];
    next[i] = { ...next[i], ...line, params: { ...(next[i].params || {}), ...(line.params || {}) } };
    return next;
}

/**
 * Fold one brand-discovery event into the notebook.
 *
 * The server narrates in English sentences ("Read 6 pages — 14 colours, 3
 * fonts, 22 images"); the few it always sends are recognised here and turned
 * into structured lines the screen can say in the vendor's language. Anything
 * unrecognised is dropped rather than shown in the wrong language.
 */
export function applyDiscoveryEvent(nb: Notebook, event: { type: string; data?: any }): Notebook {
    const msg: string = typeof event?.data?.message === 'string' ? event.data.message : '';

    if (event.type === 'thinking' && msg) {
        let m: RegExpMatchArray | null;
        if (/^Reading https?:|^Reading [^—]+…$/.test(msg) && !/— /.test(msg)) {
            return { ...nb, log: upsert(nb.log, { id: 'site', kind: 'site', status: 'running' }) };
        }
        if ((m = msg.match(/^Read (\d+) pages? — (\d+) colours?, (\d+) fonts?, (\d+) images?/))) {
            const log = upsert(nb.log, { id: 'site', kind: 'site', status: 'done' });
            return {
                ...nb,
                log: upsert(log, { id: 'pages', kind: 'pages', status: 'done', params: { pages: +m[1], colours: +m[2], fonts: +m[3], images: +m[4] } }),
            };
        }
        if (/^The website could not be read/.test(msg)) {
            return { ...nb, log: upsert(nb.log, { id: 'site', kind: 'site', status: 'failed' }) };
        }
        if ((m = msg.match(/^Looking at (instagram|facebook|pinterest|tiktok|linkedin) — @(.+?)…?$/i))) {
            const platform = m[1].toLowerCase() as SocialPlatform;
            const handle = m[2].replace(/…$/, '');
            const socials = nb.socials[platform] ? nb.socials : { ...nb.socials, [platform]: { handle, source: 'site' as const } };
            return { ...nb, socials, log: upsert(settle(nb.log), { id: `social-${platform}`, kind: 'social', status: 'running', params: { platform, handle } }) };
        }
        if ((m = msg.match(/^Looking at (\d+) of their pictures/))) {
            return { ...nb, log: upsert(settle(nb.log), { id: 'pictures', kind: 'pictures', status: 'running', params: { count: +m[1] } }) };
        }
        if ((m = msg.match(/^Kept (\d+) interior pictures?/))) {
            return { ...nb, log: upsert(upsert(nb.log, { id: 'pictures', kind: 'pictures', status: 'done' }), { id: 'scenes', kind: 'scenes', status: 'done', params: { count: +m[1] } }) };
        }
        if (/^Working out the brand/.test(msg)) {
            return { ...nb, log: upsert(settle(nb.log), { id: 'brand', kind: 'brand', status: 'running' }) };
        }
        return nb;
    }

    if (event.type === 'tool_result' && event.data?.name === 'brand_kit_updated') {
        const kit = event.data.brandKit || {};
        const report = event.data.report || {};
        const colors = [...(kit.primaryColors || []), ...(kit.secondaryColors || [])].filter((c: string) => HEX.test(c));
        const socials = { ...nb.socials };
        for (const p of report.profiles || []) {
            const platform = String(p.platform || '').toLowerCase() as SocialPlatform;
            if (!SOCIAL_PLATFORMS.includes(platform) || !p.handle) continue;
            socials[platform] = { handle: p.handle, followers: typeof p.followers === 'number' ? p.followers : undefined, source: socials[platform]?.source ?? 'site' };
        }
        for (const [platform, handle] of Object.entries(kit.socialHandles || {})) {
            const key = platform.toLowerCase() as SocialPlatform;
            if (SOCIAL_PLATFORMS.includes(key) && typeof handle === 'string' && handle && !socials[key]) socials[key] = { handle, source: 'site' };
        }
        const log = settle(nb.log);
        return {
            ...nb,
            socials,
            brand: {
                name: kit.brandName || nb.brand.name,
                logoUrl: kit.logoUrl || nb.brand.logoUrl,
                colors: colors.length ? colors.slice(0, 8) : (report.palette || []).filter((c: any) => HEX.test(c?.hex ?? c)).map((c: any) => c?.hex ?? c).slice(0, 8),
                font: kit.primaryFont || report.fonts?.[0]?.family || report.fonts?.[0] || nb.brand.font,
                tone: kit.toneOfVoice || nb.brand.tone,
                keywords: (kit.styleKeywords || []).slice(0, 6),
                promise: kit.valueProposition || nb.brand.promise,
            },
            log: upsert(log, { id: 'brand', kind: 'brand', status: 'done', params: { name: kit.brandName || '' } }),
            brandDone: true,
        };
    }

    if (event.type === 'error') {
        return { ...nb, log: settle(nb.log, 'failed'), brandDone: true };
    }
    if (event.type === 'done') return { ...nb, brandDone: true, log: settle(nb.log) };
    return nb;
}

const SEO_KINDS: Record<string, LogKind> = { overview: 'market', keywords: 'keywords', competitors: 'competitors', backlinks: 'backlinks' };

/** Fold the latest poll of the search-market read into the notebook. */
export function applyAnalysisRun(nb: Notebook, run: AnalysisRun | null): Notebook {
    if (!run) return nb;
    let log = nb.log;
    for (const step of run.steps || []) {
        const kind = SEO_KINDS[step.kind];
        if (!kind || step.status === 'pending') continue;
        const status = step.status === 'running' ? 'running' : step.status === 'done' ? 'done' : 'failed';
        log = upsert(log, { id: `seo-${step.kind}`, kind, status });
    }
    const s: any = run.summary || {};
    const overview = s.overview || {};
    const competitors: string[] = (s.competitors?.competitors || []).map((c: any) => c.domain).filter(Boolean).slice(0, 5);
    const top = (s.keywords?.keywords || []).find((k: any) => k?.keyword);
    // Numbers onto the lines themselves, so "Mapped your search market" says how big.
    log = log.map((l) => {
        if (l.id === 'seo-overview' && overview.organicKeywords != null) return { ...l, params: { keywords: overview.organicKeywords } };
        if (l.id === 'seo-competitors' && competitors.length) return { ...l, params: { count: (s.competitors?.competitors || []).length } };
        if (l.id === 'seo-backlinks' && s.backlinks?.referringDomains != null) return { ...l, params: { domains: s.backlinks.referringDomains } };
        return l;
    });
    return {
        ...nb,
        screenshot: s.screenshot?.imageUrl || nb.screenshot,
        market: {
            keywords: overview.organicKeywords ?? nb.market.keywords,
            visits: overview.estimatedMonthlyVisits ?? nb.market.visits,
            top10: overview.top10 ?? nb.market.top10,
            trafficValue: overview.trafficValue ?? nb.market.trafficValue,
            competitors: competitors.length ? competitors : nb.market.competitors,
            topKeyword: top?.keyword ?? nb.market.topKeyword,
        },
        log,
        marketDone: run.status === 'succeeded' || run.status === 'failed',
    };
}

export function applyDetection(nb: Notebook, d: PlatformDetection | null): Notebook {
    if (!d) return nb;
    const shop = { platform: d.platform, confidence: d.confidence, connectable: d.connectable };
    return { ...nb, shop, log: d.platform ? upsert(nb.log, { id: 'shop', kind: 'shop', status: 'done', params: { platform: d.platform } }) : nb.log };
}

/** How far along the reading is, 0–1, for the little ring in the corner. */
export function learningProgress(nb: Notebook): number {
    // Both readers finished: done, whatever they could not get (a failed
    // search read never brings its homepage capture).
    if (nb.brandDone && nb.marketDone) return 1;
    const parts = [nb.brandDone, nb.marketDone, Boolean(nb.shop), Boolean(nb.screenshot)];
    const running = nb.log.filter((l) => l.status === 'running').length;
    const done = parts.filter(Boolean).length;
    return Math.min(1, (done + Math.min(running, 2) * 0.15) / parts.length);
}

/**
 * What Ardi would start with, from what it read. A suggestion the vendor can
 * change, never a decision: the visualizer when the site sells surfaces or
 * furniture (every Ardealy vendor does, so it leads), Business when the search
 * read found a market worth watching, Marketing when there are socials to post
 * to, the assistant when there is a shop to answer questions for.
 */
export function suggestGoals(nb: Notebook): Goal[] {
    const out: Goal[] = ['visualizer'];
    if ((nb.market.keywords ?? 0) > 0 || nb.market.competitors.length) out.push('business');
    if (Object.keys(nb.socials).length) out.push('marketing');
    if (nb.shop?.platform) out.push('assistant');
    // Three at most: a suggestion that ticks everything is no suggestion.
    return out.slice(0, 3);
}

/** The routines each goal switches on, in the order they are listed (template ids of the API). */
const ROUTINES_BY_GOAL: Record<Goal, string[]> = {
    visualizer: ['lead_followup', 'catalogue_health'],
    assistant: ['lead_followup'],
    marketing: ['content_ideas'],
    business: ['market_watch', 'weekly_performance'],
    ads: ['weekly_performance'],
};

/** Every vendor gets the morning brief; the rest follow the goals, once each. */
export function routinesFor(goals: Goal[]): string[] {
    const ids = ['morning_brief', ...goals.flatMap((g) => ROUTINES_BY_GOAL[g] || [])];
    return [...new Set(ids)].slice(0, 5);
}

/** Toggle a goal, keeping the order the vendor picked them in (first pick = most important). */
export function toggleGoal(goals: Goal[], goal: Goal): Goal[] {
    return goals.includes(goal) ? goals.filter((g) => g !== goal) : [...goals, goal];
}

/**
 * The last screen's checklist: what is ready in each chosen workspace, said
 * with the numbers Ardi actually found. Items without data are still listed —
 * "ready when you add products" is true and worth saying — but marked so.
 */
export interface PrepItem {
    id: string;
    workspace: Goal | 'home' | 'brand';
    /** i18n key under ONBOARDING_V2.PREP. */
    key: string;
    params?: Record<string, string | number>;
}

export function preparationPlan(goals: Goal[], nb: Notebook, extras: { shopConnected?: boolean; routines: string[] }): PrepItem[] {
    const items: PrepItem[] = [];
    if (nb.brandDone && (nb.brand.colors.length || nb.brand.font || nb.brand.tone)) {
        items.push({ id: 'brand', workspace: 'brand', key: 'BRAND', params: { colours: nb.brand.colors.length, font: nb.brand.font || '—' } });
    }
    for (const g of goals) {
        if (g === 'visualizer') items.push({ id: 'visualizer', workspace: g, key: 'VISUALIZER' });
        if (g === 'assistant') items.push({ id: 'assistant', workspace: g, key: nb.website ? 'ASSISTANT' : 'ASSISTANT_NO_SITE' });
        if (g === 'marketing') items.push({ id: 'marketing', workspace: g, key: Object.keys(nb.socials).length ? 'MARKETING' : 'MARKETING_NO_SOCIALS', params: { count: Object.keys(nb.socials).length } });
        if (g === 'business') {
            items.push(
                nb.market.keywords
                    ? { id: 'business', workspace: g, key: 'BUSINESS', params: { keywords: nb.market.keywords, competitors: nb.market.competitors.length } }
                    : { id: 'business', workspace: g, key: 'BUSINESS_PENDING' },
            );
            if (extras.shopConnected) items.push({ id: 'sales', workspace: g, key: 'SALES' });
        }
        if (g === 'ads') items.push({ id: 'ads', workspace: g, key: 'ADS' });
    }
    if (extras.routines.length) items.push({ id: 'routines', workspace: 'home', key: 'ROUTINES', params: { count: extras.routines.length } });
    items.push({ id: 'home', workspace: 'home', key: 'HOME' });
    return items;
}

/* ─────────────────────────── remembered progress ─────────────────────────── */

/**
 * Where the vendor is in the journey, per vendor and per browser, so a refresh
 * or a closed tab resumes instead of restarting. The server only learns the
 * outcome (goals, socials, onboardedAt); half-answered questions stay local.
 */
export interface JourneyProgress {
    step: JourneyStep;
    website?: string;
    goals?: Goal[];
    socials?: Partial<Record<SocialPlatform, string>>;
    startedAt?: string;
}

const KEY = 'ardealy.bo.onboarding.journey';

const readAll = (): Record<string, JourneyProgress> => {
    try {
        return JSON.parse(localStorage.getItem(KEY) || '{}') || {};
    } catch {
        return {};
    }
};

export const readProgress = (vendorId?: string): JourneyProgress | null => (vendorId ? readAll()[vendorId] ?? null : null);

export const saveProgress = (vendorId: string | undefined, progress: JourneyProgress) => {
    if (!vendorId) return;
    try {
        localStorage.setItem(KEY, JSON.stringify({ ...readAll(), [vendorId]: progress }));
    } catch {
        /* resumes from the start next time; nothing is lost on the server */
    }
};

export const clearProgress = (vendorId?: string) => {
    if (!vendorId) return;
    try {
        const all = readAll();
        delete all[vendorId];
        localStorage.setItem(KEY, JSON.stringify(all));
    } catch {
        /* nothing to do */
    }
};

/** A journey started and not finished — shown again even though a website is now on file. */
export const isJourneyInProgress = (vendorId?: string): boolean => {
    const p = readProgress(vendorId);
    return Boolean(p && p.step !== 'welcome');
};

/** "@casa.ceramica" / "instagram.com/casa.ceramica" / "https://www.instagram.com/casa.ceramica/" → "casa.ceramica". */
export function cleanHandle(input: string): string {
    const v = (input || '').trim();
    if (!v) return '';
    const noScheme = v.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    const path = noScheme.includes('/') ? noScheme.split('/').filter(Boolean).slice(1).join('/') : noScheme;
    return path.replace(/^@/, '').replace(/[/?#].*$/, '').trim();
}
